import { useEffect } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import type { Role } from '@cannery/shared';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { AppShell } from './components/AppShell';
import { environmentLabel } from './components/EnvironmentBadge';
import { Spinner } from './components/ui';
import { LoginPage } from './pages/LoginPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { AdminDashboardPage } from './pages/admin/DashboardPage';
import {
  BackupPage,
  ReportsPage,
} from './pages/admin/ModulePages';
import { InventoryPage } from './pages/admin/InventoryPage';
import { PurchasingPage } from './pages/admin/PurchasingPage';
import { ExpensesPage } from './pages/admin/ExpensesPage';
import { MonitoringPage } from './pages/admin/MonitoringPage';
import { ExceptionsPage } from './pages/admin/ExceptionsPage';
import { AccountabilityPage } from './pages/admin/AccountabilityPage';
import { ComparisonPage } from './pages/admin/ComparisonPage';
import { DiscrepancyPage } from './pages/admin/DiscrepancyPage';
import { PaymentsPage } from './pages/admin/PaymentsPage';
import { ReconciliationPage } from './pages/admin/ReconciliationPage';
import { HandoverPage } from './pages/admin/HandoverPage';
import { ControlReviewPage } from './pages/admin/ControlReviewPage';
import { PickupPage } from './pages/admin/ReadyQueuePages';
import { DeliveryPage } from './pages/admin/DeliveryPage';
import { TransactionsPage } from './pages/admin/TransactionsPage';
import { SettingsPage } from './pages/admin/SettingsPage';
import { EmployeeDashboardPage } from './pages/employee/EmployeePages';
import { ProcessingQueuePage } from './pages/ProcessingQueuePage';

function Splash() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-slate-50">
      <Spinner className="h-8 w-8 text-indigo-600" />
    </div>
  );
}

/**
 * Route guard. Authentication AND authorization are enforced server-side on
 * every API call; this guard only shapes navigation so users land in the
 * right place for their role.
 */
function Protected({ role }: { role: Role }) {
  const { status, user, homePath } = useAuth();
  if (status === 'loading') return <Splash />;
  if (status !== 'authed' || !user) return <Navigate to="/login" replace />;
  if (user.role !== role) return <Navigate to={homePath} replace />;
  return <Outlet />;
}

function Shell() {
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

function RootRedirect() {
  const { status, homePath } = useAuth();
  if (status === 'loading') return <Splash />;
  if (status === 'authed') return <Navigate to={homePath} replace />;
  return <Navigate to="/login" replace />;
}

export default function App() {
  // Staging builds suffix the tab title so the environment stays obvious even
  // when the badge is scrolled out of view. Production keeps the plain title.
  useEffect(() => {
    const label = environmentLabel();
    document.title = label ? `Cannery Laundry Hub — ${label}` : 'Cannery Laundry Hub';
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<LoginPage />} />

          {/* Admin: cross-branch management */}
          <Route element={<Protected role="ADMIN" />}>
            <Route element={<Shell />}>
              <Route path="/admin" element={<AdminDashboardPage />} />
              <Route path="/admin/transactions" element={<TransactionsPage />} />
              <Route path="/admin/monitoring" element={<MonitoringPage />} />
              <Route path="/admin/exceptions" element={<ExceptionsPage />} />
              <Route path="/admin/accountability" element={<AccountabilityPage />} />
              <Route path="/admin/comparison" element={<ComparisonPage />} />
              <Route path="/admin/discrepancies" element={<DiscrepancyPage />} />
              <Route path="/admin/payments" element={<PaymentsPage />} />
              <Route path="/admin/reconciliation" element={<ReconciliationPage />} />
              <Route path="/admin/handover" element={<HandoverPage />} />
              <Route path="/admin/control-review" element={<ControlReviewPage />} />
              <Route path="/admin/washing" element={<ProcessingQueuePage area="washing" />} />
              <Route path="/admin/folding" element={<ProcessingQueuePage area="folding" />} />
              <Route path="/admin/pickup" element={<PickupPage />} />
              <Route path="/admin/delivery" element={<DeliveryPage />} />
              <Route path="/admin/purchasing" element={<PurchasingPage />} />
              <Route path="/admin/inventory" element={<InventoryPage />} />
              <Route path="/admin/expenses" element={<ExpensesPage />} />
              <Route path="/admin/reports" element={<ReportsPage />} />
              <Route path="/admin/settings" element={<SettingsPage />} />
              <Route path="/admin/backup" element={<BackupPage />} />
            </Route>
          </Route>

          {/* Washing employee: own branch only */}
          <Route element={<Protected role="WASHING_EMPLOYEE" />}>
            <Route element={<Shell />}>
              <Route path="/wash" element={<EmployeeDashboardPage role="WASHING_EMPLOYEE" />} />
              <Route path="/wash/area" element={<ProcessingQueuePage area="washing" />} />
            </Route>
          </Route>

          {/* Folding employee: own branch only */}
          <Route element={<Protected role="FOLDING_EMPLOYEE" />}>
            <Route element={<Shell />}>
              <Route path="/fold" element={<EmployeeDashboardPage role="FOLDING_EMPLOYEE" />} />
              <Route path="/fold/area" element={<ProcessingQueuePage area="folding" />} />
            </Route>
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
